import { Component, Input, ChangeDetectorRef, OnInit, OnDestroy, NgZone, ChangeDetectionStrategy } from '@angular/core';
import { AppService } from '../app.service';
import { UtilsService } from '../shared/utils.service';
import {
  MediaMatcher
} from '@angular/cdk/layout';
import { RouterService } from '../shared/router.service';
import { UserMessagesService } from '../user/user-messages/user-messages.service';
import {AuthService} from "../shared/auth.service";
import {ApiRequestService} from "../shared/api-request.service";
import { AppcuesService } from '../services/appcues.service';
import { HubspotService } from '../services/hubspot.service';
import {SentryService} from "../services/sentry.service";
import {SentryErrorTesterComponent} from "../shared/sentry-error-tester/sentry-error-tester.component";
import { AccountSwitcherComponent } from '../user/account-switcher/account-switcher.component';
import { MatButton } from '@angular/material/button';
import { RBACService } from '../shared/rbac.service';
import { StorageService } from '../shared/storage.service';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { UserAccessRequestsService } from '../user/account-access/user-access-requests.service';
import { BackgroundExportService } from '../shared/background-export.service';

@Component({
    selector: 'app-top-nav',
    templateUrl: './top-nav.component.html',
    styleUrls: ['./top-nav.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TopNavComponent implements OnInit, OnDestroy {

  @Input() sidenav: any;
  mobileQuery: MediaQueryList;

  // Quick access timer properties
  inactivityTimeRemaining: string = '';
  hardCapTimeRemaining: string = '';
  isQuickAccessWarning: boolean = false;
  isQuickAccessExpired: boolean = false;
  quickAccessInterval: any = null;
  private quickAccessSub: Subscription;

  private readonly _mobileQueryListener: () => void;

  // True while the HubSpot chat widget is loading/opening, used to show a spinner
  // on the chat button.
  chatOpening: boolean = false;

  /**
   * Settings/account menu visibility flags, each combining the subscription plan gate with the RBAC
   * check for that entry.
   *
   * Resolved once per permission change rather than inline in the template: the top navigation is
   * always rendered, so inline checks ran on every change detection cycle. This component is never
   * destroyed, so the flags are recomputed from rbac.permissionsChanged$.
   */
  menuCan = {
    settings: false,
    companyDetails: false,
    companyDocuments: false,
    rbacRoles: false,
    notificationRoles: false,
    tags: false,
    userProfile: false
  };

  /**
   * Does the user have an outstanding or refused request to join an account (SC-301)?
   *
   * Drives the Account Access entry in this menu, which is the only route back to the status page after the
   * user navigates away from it.
   */
  hasOwnAccessRequest: boolean = false;

  /**
   * Was one of those requests refused? Badges the entry, since a refusal is the state needing action and it
   * raises no shell banner.
   */
  hasDeclinedAccessRequest: boolean = false;

  private permissionsSubscription?: Subscription;
  private ownRequestsSubscription?: Subscription;

  constructor(
    public app: AppService,
    public utils: UtilsService,
    public routerService: RouterService,
    private auth: AuthService,
    private api: ApiRequestService,
    media: MediaMatcher,
    changeDetectorRef: ChangeDetectorRef,
    public userMessagesService: UserMessagesService,
    private zone: NgZone,
    public sentry: SentryService,
    public rbac: RBACService,
    private storage: StorageService,
    private appcuesService: AppcuesService,
    private router: Router,
    public backgroundExports: BackgroundExportService,
    public hubspot: HubspotService,
    private userAccessRequests: UserAccessRequestsService
  ) {
    this.mobileQuery = media.matchMedia('(max-width: 1200px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
  }

  /**
   * Resolves the settings/account menu visibility flags from the subscription plan and the user's
   * RBAC permissions. Mirrors the previous inline template checks exactly.
   *
   * @return {void}
   */
  private resolveMenuPermissions(): void {
    const F = RBACService.RBAC_F;
    const A = RBACService.RBAC_A;

    // Does the active account offer account features at all (SC-301)?
    //
    // False on the user's own personal account, which is not a workspace. Every entry in the settings
    // menu administers a company that does not exist there: Company Details for no company, RBAC Roles
    // governing one person who is already the administrator, Company Documents for a single member.
    //
    // Stated explicitly for the RBAC-only entries (settings, companyDetails, rbacRoles). The three that
    // already carry a planCan() term inherit the same rule from it, since planCan() returns false on a
    // personal account.
    //
    // userProfile is deliberately untouched. It is gated on the core-user-profile permission, which is
    // never plan gated and stays with the user on any account. It is what keeps My Tasks, My Forms, My
    // Messages, Training/Competency, Change Login Credentials and API Tokens reachable, which is exactly
    // the set a personal account is meant to retain.
    //
    // Super admins keep full access, matching planCan() and EnsurePlanFeature on the API.
    const accountFeatures: boolean = this.app.user.is_sitesoft_admin
      || this.app.accountFeaturesAvailable();

    this.menuCan = {
      // The settings button shows when the user can reach at least one entry in the settings menu.
      settings: accountFeatures && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_ACCOUNT_SETTINGS, A.A_EDIT),
        RBACService.getPermission(F.F_ACCOUNT_COMPANY_DOCUMENTS, A.A_VIEW),
        RBACService.getPermission(F.F_ACCOUNT_COMPANY_DOCUMENTS, A.A_CREATE),
        RBACService.getPermission(F.F_ACCOUNT_RBAC_ROLES, A.A_VIEW),
        RBACService.getPermission(F.F_ACCOUNT_RBAC_ROLES, A.A_CREATE),
        RBACService.getPermission(F.F_ACCOUNT_RBAC_ROLES, A.A_EDIT),
        RBACService.getPermission(F.F_ACCOUNT_RBAC_ROLES, A.A_DELETE),
        RBACService.getPermission(F.F_ACCOUNT_NOTIFICATION_ROLES, A.A_VIEW),
        RBACService.getPermission(F.F_ACCOUNT_NOTIFICATION_ROLES, A.A_CREATE),
        RBACService.getPermission(F.F_ACCOUNT_TAGS, A.A_VIEW),
        RBACService.getPermission(F.F_ACCOUNT_TAGS, A.A_CREATE)
      ]),
      companyDetails: accountFeatures && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_ACCOUNT_SETTINGS, A.A_EDIT)
      ]),
      companyDocuments: this.app.planCan(AppService.FEATURES.COMPANY_DOCUMENTS) && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_ACCOUNT_COMPANY_DOCUMENTS, A.A_VIEW),
        RBACService.getPermission(F.F_ACCOUNT_COMPANY_DOCUMENTS, A.A_CREATE)
      ], false),
      // Any RBAC roles permission reaches the page, so a create-only user can still add roles.
      rbacRoles: accountFeatures && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_ACCOUNT_RBAC_ROLES, A.A_VIEW),
        RBACService.getPermission(F.F_ACCOUNT_RBAC_ROLES, A.A_CREATE),
        RBACService.getPermission(F.F_ACCOUNT_RBAC_ROLES, A.A_EDIT),
        RBACService.getPermission(F.F_ACCOUNT_RBAC_ROLES, A.A_DELETE)
      ], false),
      notificationRoles: this.app.planCan(AppService.FEATURES.NOTIFICATION_ROLES) && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_ACCOUNT_NOTIFICATION_ROLES, A.A_VIEW),
        RBACService.getPermission(F.F_ACCOUNT_NOTIFICATION_ROLES, A.A_CREATE)
      ], false),
      tags: this.app.planCan(AppService.FEATURES.TAGS) && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_ACCOUNT_TAGS, A.A_CREATE),
        RBACService.getPermission(F.F_ACCOUNT_TAGS, A.A_VIEW)
      ]),
      userProfile: this.rbac.userCanAccess([F.CORE_PERMISSION_USER_PROFILE])
    };
  }

  ngOnInit(): void {
    // Recompute the menu flags whenever the user's permissions change. This component is never
    // destroyed, so resolving them only once would leave the menus stale after sign in (the session
    // is restored asynchronously), account switching, or super admin quick access.
    this.permissionsSubscription = this.rbac.permissionsChanged$.subscribe((): void => {
      this.resolveMenuPermissions();
    });

    // Track the user's own membership requests so the Account Access entry appears while one is
    // outstanding. AppComponent owns the fetching (it refreshes on the same permissions signal above), so
    // this only subscribes: two components fetching the same thing would double every request.
    this.ownRequestsSubscription = this.userAccessRequests.requests.subscribe(
      (requests: any[]): void => {
        this.hasOwnAccessRequest = (requests || []).length > 0;
        this.hasDeclinedAccessRequest = (requests || []).some(
          (row: any): boolean => row.approval_status === 'declined'
        );
      }
    );

    // Recalculate immediately whenever quickAccessSession changes (e.g., after quick access or account switch).
    this.quickAccessSub = this.app.quickAccessSessionChanged.subscribe(() => this.updateQuickAccessTimers());

    // Also recalculate on a 60-second interval so the countdown ticks down.
    this.updateQuickAccessTimers();
    this.quickAccessInterval = setInterval(() => this.updateQuickAccessTimers(), 60000);
  }

  ngOnDestroy(): void {
    if (this.quickAccessInterval) {
      clearInterval(this.quickAccessInterval);
    }
    if (this.quickAccessSub) {
      this.quickAccessSub.unsubscribe();
    }
    this.permissionsSubscription?.unsubscribe();
    this.ownRequestsSubscription?.unsubscribe();
    this.mobileQuery.removeListener(this._mobileQueryListener);
  }

  updateQuickAccessTimers(): void {
    const session = this.app.quickAccessSession;
    if (!session) {
      this.inactivityTimeRemaining = '';
      this.hardCapTimeRemaining = '';
      this.isQuickAccessWarning = false;
      this.isQuickAccessExpired = false;
      return;
    }

    const now = Date.now();
    const lastActiveAt = new Date(session.last_active_at).getTime();
    const createdAt = new Date(session.created_at).getTime();

    const inactivityRemainingHours = session.inactivity_timeout_hours - ((now - lastActiveAt) / 3600000);
    const hardCapRemainingHours = session.hard_max_hours - ((now - createdAt) / 3600000);

    this.inactivityTimeRemaining = this.formatTimeRemaining(inactivityRemainingHours);
    this.hardCapTimeRemaining = this.formatTimeRemaining(hardCapRemainingHours);
    this.isQuickAccessWarning = inactivityRemainingHours < 1 || hardCapRemainingHours < 1;

    // If either timer has expired and we haven't already triggered the switch-back, do it now.
    if ((inactivityRemainingHours <= 0 || hardCapRemainingHours <= 0) && !this.isQuickAccessExpired) {
      this.isQuickAccessExpired = true;
      const reason = inactivityRemainingHours <= 0 ? 'inactivity' : 'the max time limit';
      this.utils.showModal(
        'Quick Access Expired',
        `Your quick access session has expired due to ${reason}. You will be switched back to your employer account.`,
        (): void => {
          this.switchBackToEmployerAccount();
        }
      );
    }
  }

  private formatTimeRemaining(hours: number): string {
    if (hours <= 0) {
      return 'Expired';
    }
    const totalMinutes = Math.floor(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}h ${m}m`;
  }

  getAccountName(): string {
    // Always show the subscription tier alongside the account name (e.g. "Acme (Free)") when
    // authenticated and a tier is set.
    return this.getDisplayAccountName() + (this.app.isAuthenticated && this.app.account.subscription ? ' (' + this.app.account.subscription + ')' : '');
  }

  /**
   * The account name to display, substituting "My Account" for the user's own personal account (SC-301).
   *
   * A personal account stores the PERSON'S name, because that value is what appears on host site sign-in
   * records and those records must identify who actually attended. In the portal, though, showing your own
   * name where an account name belongs reads as a bug, so it is presented as "My Account".
   *
   * Presentational only. The stored name is never changed and this label is never sent to the API.
   *
   * @return {string} The account name to show in the interface.
   */
  getDisplayAccountName(): string {
    if ( this.app.isPersonalAccount(this.app.account) ) {
      return 'My Account';
    }
    return this.app.account.name;
  }

  /**
   * Opens the HubSpot live chat from the top navigation button (custom launcher).
   * Shows a spinner on the button while the widget loads/opens. A safety timeout
   * clears the spinner in case the widget never signals that it opened.
   *
   * @return {void} Does not return a value.
   */
  onOpenChat(): void {
    if ( this.chatOpening ) {
      return;
    }
    this.chatOpening = true;

    // HubSpot callbacks run outside Angular's zone, so reset inside the zone to
    // ensure change detection updates the button.
    const reset = (): void => this.zone.run((): void => { this.chatOpening = false; });
    const safetyTimeout = setTimeout(reset, 15000);

    this.hubspot.openChat().finally((): void => {
      clearTimeout(safetyTimeout);
      reset();
    });
  }

  /**
   * Signs the user out of their current session.
   *
   * @return {void} No return value.
   */
  onSignOut(): void {
    this.auth.signOut();
  }

  /**
   * Handles the opening of the account switcher overlay.
   * This method attaches an overlay with backdrop functionality to the specified button element.
   *
   * @param {MatButton} button - The button element that triggers the account switcher overlay.
   * @return {void} - Does not return a value.
   */
  onOpenAccountSwitcher(button: MatButton): void {
    this.utils.openOverlayWithBackdropClose(button._elementRef, AccountSwitcherComponent);
  }

  onSentryErrorTester(): void{
    this.utils.showComponentDialog(SentryErrorTesterComponent, {}, {
      width: '1024px'
    }, (results: any): void => {
      if ( typeof results !== 'undefined' ) {
        //
      }
    });
  }

  /**
   * Reset both quick access timers by deleting and re-creating the record.
   */
  onResetQuickAccessTimers(): void {
    this.api.makeRequest('post', `v2/admin/accounts/${this.app.account.id}/quick-access/reset`)
      .then((response: any): void => {
        this.app.quickAccessSession = response.data.quick_access_session;
        this.utils.showToast('Quick access timers have been reset.');
      })
      .catch((): void => {
        this.utils.showToast('Failed to reset quick access timers.');
      });
  }

  /**
   * Remove the quick access record so the account link becomes permanent (no timers).
   */
  onDetachQuickAccess(): void {
    this.utils.showModal(
      'Detach Quick Access',
      'This will remove the quick access timers. Your account link will remain but without any time limits. Continue?',
      (): void => {
        this.api.makeRequest('delete', `v2/admin/accounts/${this.app.account.id}/quick-access`)
          .then((response: any): void => {
            this.app.quickAccessSession = null;
            this.utils.showToast('Quick access timers detached. Account link is now permanent.');
          })
          .catch((): void => {
            this.utils.showToast('Failed to detach quick access timers.');
          });
      }
    );
  }

  /**
   * Switch the super admin back to their employer account.
   * Uses the same flow as the account switcher component.
   */
  private switchBackToEmployerAccount(): void {
    const employerAccountId = this.app.user.account_id;

    // If already on the employer account (edge case), just clear the session.
    if (this.app.account.id === employerAccountId) {
      this.app.quickAccessSession = null;
      return;
    }

    /**
     * Similar code in sc-web-portal/src/app/admin/accounts/accounts.component.ts
     * Similar code in sc-web-portal/src/app/shared/auth.service.ts
     * Similar code in sc-web-portal/src/app/user/account-switcher/account-switcher.component.ts
     */
    this.api.makeRequest('put', 'v1/managed-accounts', { id: employerAccountId })
      .then((response: any): void => {
        this.storage.set('AuthToken', response.data.api_token);
        this.storage.set('Permissions', response.data.permissions);

        this.app.user.apply(response.data.user);
        this.app.account.apply(response.data.account);
        this.app.userAccountLink.apply(response.data.user_account_link);

        // Apply permissions last: userCanAccess() also reads app.user.is_sitesoft_admin, and applying
        // permissions emits the change notification that components use to recompute their flags.
        this.rbac.applyPermissions(response.data.permissions);

        this.sentry.setActiveAccount(this.app.account.id);

        this.appcuesService.onIdentifyGroup(this.app.account.name, {
          name: this.app.account.name,
          industry: this.app.account.industry,
          address: this.app.account.address,
          suburb: this.app.account.suburb,
          city: this.app.account.city,
          postal_code: this.app.account.postal_code,
          country: this.app.account.country
        });

        this.app.quickAccessSession = response.data.quick_access_session || null;

        this.router.navigate(['/dashboard']);

        this.utils.showToast(`Switched back to ${this.app.account.name}.`);
      })
      .catch((): void => {
        this.utils.showToast('Failed to switch back to your employer account. Please switch manually.');
      });
  }

  /**
   * Stop every active background-export polling loop. The exports still
   * complete on the server (the queue job is independent of the browser);
   * the user just stops watching for them. The email and the My Exports
   * list remain as fallback delivery mechanisms.
   */
  onCancelAllBackgroundExports(): void {
    this.utils.showModal(
      'Stop polling for exports?',
      'The exports will still complete on the server and appear on the My Exports page. You will also receive an email for each one when it is ready.',
      () => {
        this.backgroundExports.cancelAll();
        this.utils.showToast('Stopped polling for all in-flight exports.');
      }
    );
  }

  protected readonly RBACService = RBACService;
  protected readonly AppService = AppService;
}
